/**
 * Export Service
 * Handles exporting data to Excel/CSV formats
 */

/**
 * Convert data to CSV format
 */
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) {
    alert('No data to export')
    return
  }

  // Get headers from first object
  const headers = Object.keys(data[0])
  
  // Create CSV content
  const csvContent = [
    // Headers
    headers.map(h => `"${h}"`).join(','),
    // Data rows
    ...data.map(row => 
      headers.map(header => {
        const value = row[header]
        // Handle null/undefined
        if (value === null || value === undefined) return '""'
        // Handle objects/arrays
        if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`
        // Handle strings with quotes
        return `"${String(value).replace(/"/g, '""')}"`
      }).join(',')
    )
  ].join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  URL.revokeObjectURL(url)
}

/**
 * Convert data to Excel format (XLSX)
 * Uses SheetJS library if available, otherwise falls back to CSV
 */
export const exportToExcel = async (data, filename = 'export.xlsx', sheetName = 'Sheet1') => {
  if (!data || data.length === 0) {
    alert('No data to export')
    return
  }

  try {
    // Try to use SheetJS if available
    const XLSX = await import('xlsx').catch(() => null)
    
    if (XLSX) {
      // Create workbook
      const workbook = XLSX.utils.book_new()
      
      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(data)
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
      
      // Write file
      XLSX.writeFile(workbook, filename)
    } else {
      // Fallback to CSV
      console.warn('SheetJS not available, falling back to CSV')
      exportToCSV(data, filename.replace('.xlsx', '.csv'))
    }
  } catch (error) {
    console.error('Error exporting to Excel:', error)
    // Fallback to CSV
    exportToCSV(data, filename.replace('.xlsx', '.csv'))
  }
}

/**
 * Export report data
 */
export const exportReport = async (reportData, format = 'csv', filename = null) => {
  const { data, fields, name } = reportData
  
  if (!data || data.length === 0) {
    alert('No data to export')
    return
  }

  // Generate filename if not provided
  if (!filename) {
    const timestamp = new Date().toISOString().split('T')[0]
    const reportName = name || 'report'
    filename = `${reportName}_${timestamp}.${format}`
  }

  // Select only requested fields if specified
  let exportData = data
  if (fields && fields.length > 0) {
    exportData = data.map(row => {
      const filtered = {}
      fields.forEach(field => {
        if (row[field] !== undefined) {
          filtered[field] = row[field]
        }
      })
      return filtered
    })
  }

  // Export based on format
  if (format === 'csv') {
    exportToCSV(exportData, filename)
  } else if (format === 'xlsx' || format === 'excel') {
    await exportToExcel(exportData, filename)
  } else {
    throw new Error(`Unsupported export format: ${format}`)
  }
}

/**
 * Export table data
 */
export const exportTable = async (tableData, tableName, format = 'csv') => {
  if (!tableData || tableData.length === 0) {
    alert('No data to export')
    return
  }

  const timestamp = new Date().toISOString().split('T')[0]
  const filename = `${tableName}_${timestamp}.${format === 'csv' ? 'csv' : 'xlsx'}`

  if (format === 'csv') {
    exportToCSV(tableData, filename)
  } else {
    await exportToExcel(tableData, filename)
  }
}

export default {
  exportToCSV,
  exportToExcel,
  exportReport,
  exportTable,
}

